import React from "react";
import { PenTool, MessageSquare, LayoutDashboard, Bot, ArrowUpRight } from "lucide-react";

const IMG = {
  engine:
    "https://static.prod-images.emergentagent.com/jobs/a4ad1d97-7bf7-4aea-b21a-c6bda523e1eb/images/10631b6170c6957a1afadf05cf3cf95b3a2bbd18bc389c84144e1bb45ac74223.png",
  dm: "https://static.prod-images.emergentagent.com/jobs/a4ad1d97-7bf7-4aea-b21a-c6bda523e1eb/images/4e55e05249c85b134a17cad181a2a412d22718e6ee9fd5c6676a5c7999674e6e.png",
  mogul:
    "https://static.prod-images.emergentagent.com/jobs/a4ad1d97-7bf7-4aea-b21a-c6bda523e1eb/images/0f838b30a90d5138163c482752c46bfb8d418a815a9960fb089af60531e94700.png",
  assistant:
    "https://static.prod-images.emergentagent.com/jobs/a4ad1d97-7bf7-4aea-b21a-c6bda523e1eb/images/9787ac5de75c1a7dd2772bb2f754cbaf484ffb8ce98272e383ae330943e566c0.png",
};

const FEATURES = [
  {
    id: "content-engine",
    title: "The Content Engine",
    icon: PenTool,
    tag: "01 — Content",
    image: IMG.engine,
    desc: "One-click AI blog & news generation, SEO-tightened, then auto-pushed to a network of high-authority distribution partners. No more cold publishing.",
    bullets: [
      "Topic → finished post in under 60s",
      "Schema-marked for Google E-E-A-T",
      "Auto-syndicated to partner networks",
    ],
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    id: "dm-closer",
    title: "The DM Closer",
    icon: MessageSquare,
    tag: "02 — Conversion",
    image: IMG.dm,
    desc: "Official Meta-verified Instagram & Facebook DM automation. Comment → DM → close. Under 1 second. Every time.",
    bullets: [
      "Sub-second comment-to-DM trigger",
      "Meta verified, never shadowbanned",
      "Smart objection-handling scripts",
    ],
    span: "lg:col-span-5",
  },
  {
    id: "mogul-sync",
    title: "The Mogul Sync",
    icon: LayoutDashboard,
    tag: "03 — Distribution",
    image: IMG.mogul,
    desc: "One dashboard to sync your ads across hundreds of high-traffic local news and community sites. Local domination, on autopilot.",
    bullets: [
      "Local newsroom + community network",
      "Cross-site analytics in one view",
      "Pause / boost in one click",
    ],
    span: "lg:col-span-5",
  },
  {
    id: "assistant",
    title: "The ClydeFlow Assistant",
    icon: Bot,
    tag: "04 — Execution",
    image: IMG.assistant,
    desc: "Not another chatbot that ideates. A direct-execution executive AI that books, sends, syncs, posts, files — and actually finishes the job.",
    bullets: [
      "Connects to your real tools",
      "Writes and ships, not suggests",
      "Daily ops handled in the background",
    ],
    span: "lg:col-span-7",
  },
];

const FeatureCard = ({ f }) => {
  const Icon = f.icon;
  return (
    <article
      data-testid={`feature-card-${f.id}`}
      className={`group relative ${f.span} bg-[#0A192F] border border-white/10 hover:border-cyan-400/50 hover:-translate-y-1 transition-all duration-300 overflow-hidden rounded-sm`}
    >
      <div className="aspect-[16/9] overflow-hidden bg-[#050B14] relative">
        <img
          src={f.image}
          alt={f.title}
          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-transparent to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em]">
          <Icon size={14} />
          {f.tag}
        </div>
      </div>

      <div className="p-6 lg:p-8">
        <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white tracking-tight">
          {f.title}
        </h3>
        <p className="mt-3 text-slate-300 leading-relaxed text-sm lg:text-base">
          {f.desc}
        </p>
        <ul className="mt-5 space-y-2">
          {f.bullets.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-slate-400 text-sm"
              data-testid={`feature-bullet-${f.id}-${i}`}
            >
              <span className="mt-1.5 h-1 w-1 rounded-full bg-cyan-400 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      </div>

      <ArrowUpRight
        size={20}
        className="absolute top-5 right-5 text-cyan-300/50 group-hover:text-cyan-300 group-hover:rotate-12 transition-all"
      />
    </article>
  );
};

const Features = () => {
  return (
    <section id="features" className="relative py-24 lg:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-4">
            What's in the box
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]">
            Four engines. <br />
            <span className="text-slate-500">One ruthless suite.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Every module is built to <span className="text-white font-semibold">execute</span>,
            not ideate. You give the input. ClydeFlow finishes the job.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 auto-rows-min">
          {FEATURES.map((f) => (
            <FeatureCard key={f.id} f={f} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
